import { useState, type FormEvent } from 'react'
import css from './views.module.css'
import { useTheme } from '../app/theme'
import { useAuth } from '../app/AuthProvider'

/** The Microsoft mark is four coloured squares, not an icon dependency. */
function MicrosoftMark() {
  return (
    <span className={css.msGrid} aria-hidden="true">
      <span style={{ background: '#F25022' }} />
      <span style={{ background: '#7FBA00' }} />
      <span style={{ background: '#00A4EF' }} />
      <span style={{ background: '#FFB900' }} />
    </span>
  )
}

export function SignIn() {
  const { theme, toggle } = useTheme()
  const { sendMagicLink } = useAuth()
  const [showEmail, setShowEmail] = useState(false)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setBusy(true)
    setError(null)
    try {
      await sendMagicLink(email.trim())
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the link.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={css.signinWrap}>
      <div className={css.signinInner}>
        <div className={css.lockup}>
          <img src="/logo-full.png" alt="98 Ventures" height={40} style={{ width: 'auto' }} />
          <span className={css.wordmark}>98 Ventures</span>
        </div>

        <div className={css.signinCard}>
          <h1 className={css.signinTitle}>Robotica</h1>
          <p className={css.signinBody}>
            Internal tool. Access is limited to the{' '}
            <span className="mono" style={{ fontSize: 12 }}>98ventures.com</span> tenant.
          </p>

          <button className={css.msButton} onClick={() => setShowEmail(true)} disabled={showEmail}>
            <MicrosoftMark />
            Continue with Microsoft 365
          </button>

          {/* ---------------------------------------------------------------
              TEMPORARY: magic-link plumbing behind the SSO button.

              The Entra app registration is with IT. Until it lands, this block
              collects the address Supabase needs to mail a link to. When the
              Azure provider is enabled in Supabase, delete this whole block and
              change the button's onClick to signInWithMicrosoft (already in
              AuthProvider). Nothing server-side changes: the RLS policies and
              grants are written against the `authenticated` role, not against
              named users.
              --------------------------------------------------------------- */}
          {showEmail ? (
            <div className={css.tempBlock}>
              <p className={css.tempLabel}>
                Single sign-on is still being set up. Until it is, we will email you a link.
              </p>
              {sent ? (
                <p className={css.tempSent}>
                  Link sent to <span className="mono">{email}</span>. It opens this dashboard
                  directly — you can close this tab.
                </p>
              ) : (
                <form className={css.tempRow} onSubmit={submit}>
                  <input
                    className={css.input}
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@98ventures.com"
                    aria-label="Work email address"
                  />
                  <button className={css.msButton} style={{ width: 'auto' }} type="submit" disabled={busy}>
                    {busy ? 'Sending' : 'Send'}
                  </button>
                </form>
              )}
              {error ? <p className={css.tempSent}>{error}</p> : null}
            </div>
          ) : null}
          {/* --------------------------- END TEMPORARY --------------------- */}

          <p className={css.footnote}>
            Single sign-on only. If your account isn&rsquo;t in the tenant directory, ask an owner
            to add you.
          </p>
        </div>

        <div className={css.signinBelow}>
          <button
            onClick={toggle}
            style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', padding: 0 }}
          >
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </button>
        </div>
      </div>
    </div>
  )
}
